import type { NavigationGuardWithThis } from 'vue-router'
import { useAuthStore, type UserRole } from '@/stores/auth'

/**
 * `admin-viewer` (read-only admin, see stores/auth.ts's UserRole comment)
 * is allowed onto any route gated for `admin` or `staff` — it can browse
 * everywhere either of those can, since actual write attempts are rejected
 * server-side (firestore.rules/Cloud Functions) regardless of what route
 * it's standing on. Route access is a navigation concern only; it is never
 * the thing enforcing "view only."
 */
function roleSatisfiesRequirement(userRole: UserRole, allowedRoles: UserRole[]): boolean {
  if (allowedRoles.includes(userRole)) return true
  if (userRole === 'admin-viewer' && (allowedRoles.includes('admin') || allowedRoles.includes('staff'))) {
    return true
  }
  return false
}

export function useRoleGuard(): NavigationGuardWithThis<undefined> {
  return async (to) => {
    const requiredRole = to.meta.requiresRole as UserRole | UserRole[] | undefined
    if (!requiredRole) return true

    const auth = useAuthStore()
    // onAuthStateChanged resolves asynchronously, so on a hard refresh
    // isReady can still be false even for an already-logged-in user —
    // wait for it instead of bouncing them to /login.
    while (!auth.isReady) {
      await new Promise((resolve) => setTimeout(resolve, 25))
    }

    const allowedRoles = Array.isArray(requiredRole) ? requiredRole : [requiredRole]

    if (!auth.isAuthenticated) {
      return { name: 'login', query: { redirect: to.fullPath } }
    }
    if (!roleSatisfiesRequirement(auth.role, allowedRoles)) {
      // Not `{ name: 'not-found' }` — that route is the wildcard
      // `/:pathMatch(.*)*`, and redirecting to it *by name* without
      // supplying the required `pathMatch` param fails to resolve, so
      // Vue Router silently aborts the navigation and the user is just
      // left stuck on whatever page they clicked from with zero feedback.
      // A literal path still matches the same wildcard route.
      return { path: '/not-found' }
    }
    return true
  }
}
